import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { type ReactNode, useState } from "react";

import { FilePreview } from "./FilePreview";
import { extensionFromName } from "./util/extensionFromName";

interface FullScreenDocumentPreviewProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileExtension?: string;
  title?: string;
  sidePanel?: ReactNode;
}

const SIDE_PANEL_WIDTH = 360;

export function FullScreenDocumentPreview({
  open,
  onClose,
  fileUrl,
  fileExtension,
  title = "Document Preview",
  sidePanel,
}: FullScreenDocumentPreviewProps) {
  const ext = fileExtension ?? extensionFromName(fileUrl.split("?")[0]);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  return (
    <SafeDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          width: "95vw",
          height: "95vh",
          maxWidth: "95vw",
          maxHeight: "95vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="span" noWrap>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {sidePanel && (
            <Tooltip title={sidePanelOpen ? "Hide comments" : "Show comments"}>
              <IconButton
                aria-label="toggle comments"
                onClick={() => setSidePanelOpen((prev) => !prev)}
                color={sidePanelOpen ? "primary" : "default"}
                size="small"
              >
                <ChatBubbleOutline />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close Preview">
            <IconButton aria-label="close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
          <Box sx={{ position: "absolute", inset: 0 }}>
            <FilePreview fileUrl={fileUrl} fileExtension={ext} />
          </Box>
        </Box>
        {sidePanel && sidePanelOpen && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box
              sx={{
                width: SIDE_PANEL_WIDTH,
                minWidth: SIDE_PANEL_WIDTH,
                height: "100%",
                overflowY: "auto",
                p: 2,
              }}
            >
              {sidePanel}
            </Box>
          </>
        )}
      </DialogContent>
    </SafeDialog>
  );
}
